import { Button, confirm } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopAddSellableProductModal } from "@/modules/merchant/presentation/ui/ShopAddSellableProductModal";
import {
  CATALOG_FILTER_CATEGORY_FIELD,
  CATALOG_FILTER_CURRENCY_FIELD,
  CATALOG_FILTER_FITMENT_FIELD,
  CATALOG_FILTER_PRODUCT_TYPE_FIELD,
  CATALOG_FILTER_Q_FIELD,
  ShopCatalogFilter,
} from "@/modules/merchant/presentation/ui/ShopCatalogFilter";
import { ShopCatalogTable } from "@/modules/merchant/presentation/ui/ShopCatalogTable";
import { ShopSkuGalleryModal } from "@/modules/merchant/presentation/ui/ShopSkuGalleryModal";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { findCondition } from "@/shared/contracts/query-helpers";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ShopSellableCatalogRow } from "../api/client";
import {
  useDeleteSellableMutation,
  useSetSellableActiveMutation,
  useShopSellableCatalogQuery,
  useShopSkuMediaCounts,
} from "../api/queries";

export function ShopCatalogPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: "/app/shops/$shopId/catalog" });
  const search = (useSearch({
    from: "/app/shops/$shopId/catalog",
  }) ?? {}) as OffsetPageQueryDTO;

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const setActive = useSetSellableActiveMutation(shopId);
  const deleteSellable = useDeleteSellableMutation(shopId);
  const qFilter = findCondition(filters, CATALOG_FILTER_Q_FIELD);
  const categoryFilter = findCondition(filters, CATALOG_FILTER_CATEGORY_FIELD);
  const productTypeFilter = findCondition(
    filters,
    CATALOG_FILTER_PRODUCT_TYPE_FIELD,
  );
  const fitmentFilter = findCondition(filters, CATALOG_FILTER_FITMENT_FIELD);
  const currencyFilter = findCondition(filters, CATALOG_FILTER_CURRENCY_FIELD);
  const q =
    typeof qFilter?.value === "string" && qFilter.value.trim() !== ""
      ? qFilter.value.trim()
      : undefined;
  const categoryId =
    categoryFilter?.value != null && String(categoryFilter.value).trim() !== ""
      ? String(categoryFilter.value)
      : undefined;
  const productType =
    productTypeFilter?.value === "part_new" ||
    productTypeFilter?.value === "part_oem" ||
    productTypeFilter?.value === "part_aftermarket" ||
    productTypeFilter?.value === "part_used" ||
    productTypeFilter?.value === "service"
      ? productTypeFilter.value
      : undefined;
  const fitment =
    fitmentFilter?.value === "pending" || fitmentFilter?.value === "complete"
      ? fitmentFilter.value
      : undefined;
  const currency =
    currencyFilter?.value === "LAK" ||
    currencyFilter?.value === "THB" ||
    currencyFilter?.value === "USD"
      ? currencyFilter.value
      : undefined;

  const list = useShopSellableCatalogQuery(shopId, {
    offset: search.offset,
    limit: search.limit,
    q,
    categoryId,
    productType,
    fitment,
    currency,
  });
  const items = list.data?.data ?? [];
  const meta = list.data?.meta;
  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;

  // prefetch จำนวนรูปของทุกแถว — ไม่บล็อกการ render ตาราง
  const sellableIds = useMemo(() => items.map((r) => r.id), [items]);
  const mediaCounts = useShopSkuMediaCounts(shopId, sellableIds);

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [pickCatalogModalOpen, setPickCatalogModalOpen] = useState(false);
  const [galleryRow, setGalleryRow] = useState<ShopSellableCatalogRow | null>(
    null,
  );
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canUpdateCatalog = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.catalog.update,
    verifiedLevel,
  );
  const canDeleteCatalog = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.catalog.delete,
    verifiedLevel,
  );
  const canAddSellableProduct = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.sell,
    verifiedLevel,
  );

  return (
    <RequireMerchantPermission permission={MerchantPermissions.catalog.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.catalog(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ສິນຄ້າທີ່ຮ້ານຂາຍໄດ້
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງສິນຄ້າທີ່ມອບໃຫ້ຮ້ານນີ້ ແລະ SKU ທີ່ເປີດໃຊ້ງານ — ຂໍ້ມູນຫຼັກຈາກ catalog, ລາຄາ/SKU
              ຕາມຮ້ານ
            </p>
          </div>
          {canAddSellableProduct ? (
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-9 gap-1"
                onClick={() => setPickCatalogModalOpen(true)}
              >
                <PlusIcon className="size-4" />
                ເພີ່ມຈາກ catalog ກາງ
              </Button>
              <Button
                type="button"
                className="h-9 gap-1"
                onClick={() => setRequestModalOpen(true)}
              >
                <PlusIcon className="size-4" />
                ເພີ່ມສິນຄ້າໃໝ່
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ShopCatalogFilter />
          <ShopCatalogTable
            data={items}
            isLoading={list.isLoading}
            offset={offset}
            limit={limit}
            totalCount={meta?.total ?? 0}
            canUpdate={canUpdateCatalog}
            mediaCounts={mediaCounts}
            onViewImages={setGalleryRow}
            onEdit={(r) => {
              if (shopId) {
                nav({
                  to: "/app/shops/$shopId/catalog/sellable/$shopProductSkuId/edit",
                  params: { shopId, shopProductSkuId: r.id },
                });
              }
            }}
            onToggleActive={(r) =>
              setActive.mutate({
                shopProductSkuId: r.id,
                isActive: !r.isActive,
              })
            }
            onDelete={
              canDeleteCatalog
                ? async (r) => {
                    const ok = await confirm({
                      title: "ລຶບສິນຄ້າອອກຈາກຮ້ານ?",
                      description: `ລຶບ "${r.productName}" (SKU ${r.shopSku})? ຖ້າສິນຄ້ານີ້ຖືກໃຊ້/ຂາຍແລ້ວ ຈະລຶບບໍ່ໄດ້.`,
                      actionText: "ລຶບ",
                      ActionProps: { variant: "destructive" },
                    });
                    if (ok) deleteSellable.mutate(r.id);
                  }
                : undefined
            }
            onPaginationChange={(nextOffset, nextLimit) =>
              shopId &&
              nav({
                to: "/app/shops/$shopId/catalog",
                params: { shopId },
                search: { ...search, offset: nextOffset, limit: nextLimit },
              })
            }
          />
        </div>

        {shopId ? (
          <>
            <ShopAddSellableProductModal
              open={requestModalOpen}
              onOpenChange={setRequestModalOpen}
              shopId={shopId}
            />
            <ShopAddSellableProductModal
              open={pickCatalogModalOpen}
              onOpenChange={setPickCatalogModalOpen}
              shopId={shopId}
              defaultEntryMode="pick"
            />
            {galleryRow ? (
              <ShopSkuGalleryModal
                shopId={shopId}
                row={galleryRow}
                onClose={() => setGalleryRow(null)}
              />
            ) : null}
          </>
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}
